import React, { useEffect, useMemo, useState, useRef } from "react";
import styled from "styled-components";
import Navbar from "./Navbar";
import DashboardHeader from "./DashboardHeader";
import Footer from "./Footer";
import api from "../api";
import { useTheme } from "../App"; // 🔥 Import the global theme hook


/* ================= THEME / LAYOUT ================= */
const Container = styled.div`
  --sidebar-expanded: 260px;
  --sidebar-collapsed: 90px;
  
  /* 🔥 THEME VARIABLES 🔥 */
  --page-bg: ${({ $isDarkMode }) => $isDarkMode ? 'linear-gradient(180deg, #0f172a 0%, #1e293b 100%)' : 'linear-gradient(180deg, #f8fafc 0%, #edf4ff 100%)'};
  --card-bg: ${({ $isDarkMode }) => $isDarkMode ? '#1e293b' : 'rgba(255, 255, 255, 0.94)'};
  --text-main: ${({ $isDarkMode }) => $isDarkMode ? '#f8fafc' : '#0f172a'};
  --text-soft: ${({ $isDarkMode }) => $isDarkMode ? '#94a3b8' : '#64748b'};
  --border: ${({ $isDarkMode }) => $isDarkMode ? 'rgba(255, 255, 255, 0.1)' : 'rgba(148, 163, 184, 0.18)'};
  --input-bg: ${({ $isDarkMode }) => $isDarkMode ? '#0f172a' : '#ffffff'};
  --table-header-bg: ${({ $isDarkMode }) => $isDarkMode ? '#0f172a' : '#f8fafc'};
  --table-border: ${({ $isDarkMode }) => $isDarkMode ? '#334155' : '#e2e8f0'};
  
  /* Custom Purple/Green Theme Vars for this page */
  --eval-th-bg: ${({ $isDarkMode }) => $isDarkMode ? 'rgba(139, 92, 246, 0.1)' : '#f5f3ff'};
  --eval-border: ${({ $isDarkMode }) => $isDarkMode ? '#4c1d95' : '#ede9fe'};
  --serial-bg: ${({ $isDarkMode }) => $isDarkMode ? 'rgba(139, 92, 246, 0.15)' : '#f5f3ff'};
  --attr-bg: ${({ $isDarkMode }) => $isDarkMode ? 'rgba(139, 92, 246, 0.1)' : '#f5f3ff'};
  --calc-staff-bg: ${({ $isDarkMode }) => $isDarkMode ? 'linear-gradient(135deg, #2e1065 0%, #4c1d95 100%)' : 'linear-gradient(135deg, #faf5ff 0%, #f5f3ff 100%)'};
  --calc-sup-bg: ${({ $isDarkMode }) => $isDarkMode ? 'linear-gradient(135deg, #064e3b 0%, #14532d 100%)' : 'linear-gradient(135deg, #f0fdf4 0%, #dcfce7 100%)'};

  --primary: #7c3aed;
  --primary-dark: #6d28d9;
  --accent: #8b5cf6;
  --shadow-lg: 0 22px 55px rgba(0, 0, 0, 0.15);
  --shadow-md: 0 12px 30px rgba(0, 0, 0, 0.1);

  min-height: 100vh;
  background: var(--page-bg);
  color: var(--text-main);
  margin-left: ${({ collapsed }) => collapsed ? "var(--sidebar-collapsed)" : "var(--sidebar-expanded)"};
  transition: margin-left 0.3s ease, background 0.3s ease, color 0.3s ease;

  @media (max-width: 1024px) { margin-left: ${({ collapsed }) => (collapsed ? "78px" : "220px")}; }
  @media (max-width: 768px) { margin-left: 0; }
`;

const Main = styled.main`
  width: min(1380px, calc(100% - 32px)); margin: 0 auto; padding: 12px 0 40px;
  @media (max-width: 1024px) { width: min(100%, calc(100% - 24px)); padding: 86px 0 32px; }
      @media (max-width: 1024px) {
    width: min(100%, calc(100% - 24px));
    padding: 12px 0 32px;
  }

  @media (max-width: 576px) {
    width: min(100%, calc(100% - 16px));
    padding: 12px 0 24px;
  }
`;

/* ================= HERO GRID ================= */
const Hero = styled.section`
  display: grid; grid-template-columns: 1.35fr 0.95fr; gap: 24px; margin-bottom: 24px;
  @media (max-width: 992px) { grid-template-columns: 1fr; }
`;

const HeroPanel = styled.div`
  background: linear-gradient(135deg, #0f172a 0%, #312e81 50%, #7c3aed 100%); color: white;
  padding: 28px; border-radius: 28px; box-shadow: var(--shadow-lg); position: relative;
  overflow: hidden; display: flex; flex-direction: column; justify-content: center; height: 100px;
  &::before { content: ""; position: absolute; right: -30px; bottom: -60px; width: 180px; height: 180px; border-radius: 50%; background: rgba(255, 255, 255, 0.08); }
  @media (max-width: 576px) { padding: 22px 18px; border-radius: 22px; }
  
`;

const HeroTitle = styled.h1` margin: 0; font-size: clamp(1.6rem, 1.2rem + 1.5vw, 2.5rem); line-height: 1.1; font-weight: 800; margin-bottom: 3px; position: relative; z-index: 1; `;

/* ================= UPDATED EMPTY STATE STYLES ================= */
const SectionTitleWrap = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 50vh; /* Gives it vertical height so it centers on the page */
  width: 100%;
  text-align: center;
`;

const SectionTitle = styled.h2`
  font-size: clamp(1.5rem, 1.2rem + 1vw, 2rem);
  font-weight: 800;
  color: var(--text-soft); /* Using text-soft makes empty states look better */
  margin: 0;
  padding: 20px;
  background: var(--card-bg);
  border: 1px dashed var(--border);
  border-radius: 20px;
  width: 100%;
  max-width: 400px;
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.03);
`;


/* Custom Select Dropdown - Compact & No Scroll */
const DropdownContainer = styled.div` position: relative; width: ${({ width }) => width || "90px"}; outline: none; z-index: ${({ $isOpen }) => ($isOpen ? 9999 : 1)}; `;
const DropdownSelected = styled.div`
  width: 100%; padding: 4px 6px; border-radius: 6px; 
  border: 1px solid ${({ $isOpen, $isDarkMode }) => ($isOpen ? "#c4b5fd" : $isDarkMode ? "#334155" : "#dbe3ef")};
  background: ${({ $disabled, $isDarkMode }) => ($disabled ? ($isDarkMode ? "var(--border)" : "#f8fafc") : "var(--input-bg)")};
  color: ${({ $disabled, $isDarkMode }) => ($disabled ? "var(--text-soft)" : "var(--text-main)")};
  font-size: 0.75rem; cursor: ${({ $disabled }) => ($disabled ? "not-allowed" : "pointer")};
  display: flex; justify-content: space-between; align-items: center; gap: 4px;
  box-shadow: ${({ $isOpen }) => ($isOpen ? "0 0 0 3px rgba(139, 92, 246, 0.12)" : "none")};
  transition: border-color 0.2s ease, box-shadow 0.2s ease;
  span:first-child { white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
  span:last-child { flex-shrink: 0; }
`;
const DropdownMenu = styled.div` position: absolute; top: calc(100% + 4px); left: 0; width: max-content; min-width: 100%; max-width: 180px; background: var(--card-bg); border: 1px solid var(--border); border-radius: 6px; box-shadow: 0 10px 25px rgba(0, 0, 0, 0.5); z-index: 999; `;
const DropdownItem = styled.div` padding: 6px 8px; font-size: 0.75rem; color: var(--text-main); cursor: pointer; border-bottom: 1px solid var(--border); &:last-child { border-bottom: none; } &:hover { background: var(--table-header-bg); color: #6d28d9; font-weight: 600; } `;

const CustomSelect = ({ value, onChange, options, disabled, width, isDarkMode }) => {
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => { if (containerRef.current && !containerRef.current.contains(event.target)) { setIsOpen(false); } };
    if (isOpen) { document.addEventListener("mousedown", handleClickOutside); }
    return () => { document.removeEventListener("mousedown", handleClickOutside); };
  }, [isOpen]);
//ModalContent
  return (
    <DropdownContainer width={width} ref={containerRef} $isOpen={isOpen}>
      <DropdownSelected $disabled={disabled} $isOpen={isOpen} $isDarkMode={isDarkMode} onClick={() => !disabled && setIsOpen(!isOpen)}>
        <span>{value === "" ? "Select" : value}</span>
        <span style={{ fontSize: "8px", color: "var(--text-soft)" }}>▼</span>
      </DropdownSelected>
      {isOpen && (
        <DropdownMenu>
          {options.map((opt) => (
            <DropdownItem key={opt.value} onMouseDown={(e) => { e.preventDefault(); onChange(opt.value); setIsOpen(false); }}>
              {opt.label}
            </DropdownItem>
          ))}
        </DropdownMenu>
      )}
    </DropdownContainer>
  );
};

const Textarea = styled.textarea`
  width: 100%; min-height: 60px; padding: 8px; border: 1px solid var(--input-border); border-radius: 8px; resize: vertical; font-size: 0.85rem; line-height: 1.4;
  background: ${({ readOnly }) => (readOnly ? "var(--border)" : "var(--input-bg)")};
  color: ${({ $textColor, readOnly }) => ($textColor ? $textColor : (readOnly ? "var(--text-soft)" : "var(--text-main)"))};
  outline: none; box-sizing: border-box; transition: all 0.2s;
  &:focus { border-color: #c4b5fd; box-shadow: ${({ readOnly }) => (readOnly ? "none" : "0 0 0 3px rgba(139, 92, 246, 0.12)")}; }
  &:read-only { cursor: not-allowed; }
`;




/* ================= COMPONENT ================= */
const Compliance = ({ user, collapsed, setCollapsed }) => {
  const [loading, setLoading] = useState(true);

  // 🔥 GLOBAL THEME HOOK 🔥
  const { isDarkMode } = useTheme();



  // if (loading) {
  //   return (
  //     <Container collapsed={collapsed} $isDarkMode={isDarkMode}>
  //       <Navbar user={user} collapsed={collapsed} setCollapsed={setCollapsed} />
  //       <DashboardHeader title="Compliance" />
  //       <CenterState>
  //         <StateCard>
  //           <Spinner />
  //           <StateTitle>Loading compliance appraisal</StateTitle>
  //           <StateText>Please wait while your records for {selectedMonth} are being prepared.</StateText>
  //         </StateCard>
  //       </CenterState>
  //     </Container>
  //   );
  // }

  return (
    // 🔥 PASS THE THEME STATE TO THE CONTAINER 🔥
    <Container collapsed={collapsed} $isDarkMode={isDarkMode}>
      <Navbar user={user} collapsed={collapsed} setCollapsed={setCollapsed} />
      <DashboardHeader user={user} collapsed={collapsed} title="Compliance" />
      

      <Main>
        {/* <Hero> */}
          <HeroPanel>
            <HeroTitle>Discipleship</HeroTitle>
          </HeroPanel>
        {/* </Hero> */}
        <SectionTitleWrap>
              <SectionTitle>No content yet</SectionTitle>
        </SectionTitleWrap>

     </Main>
      <Footer />
    </Container>
  );
};

export default Compliance;